import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';

export const DEFAULT_CLASSES = [
  { id: 'prepa', name: 'Préparatoire', shortName: 'Prépa', order: 0, status: 'active' },
  { id: '1ere', name: '1ère année', shortName: '1ère', order: 1, status: 'active' },
  { id: '2eme', name: '2ème année', shortName: '2ème', order: 2, status: 'active' },
  { id: '3eme', name: '3ème année', shortName: '3ème', order: 3, status: 'active' },
  { id: '4eme', name: '4ème année', shortName: '4ème', order: 4, status: 'active' },
  { id: '5eme', name: '5ème année', shortName: '5ème', order: 5, status: 'active' },
  { id: '6eme', name: '6ème année', shortName: '6ème', order: 6, status: 'active' }
];

export function normalizeClassText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]/g, '');
}

export function resolveLegacyClassId(value, classes = DEFAULT_CLASSES) {
  const raw = normalizeClassText(value);
  if (!raw) return '';

  const exact = classes.find((item) => (
    normalizeClassText(item.id) === raw ||
    normalizeClassText(item.name) === raw ||
    normalizeClassText(item.className) === raw ||
    normalizeClassText(item.shortName) === raw
  ));
  if (exact) return exact.id;

  if (raw.includes('preparatoire') || raw.includes('prepa')) return 'prepa';
  if (raw.includes('1ere') || raw.includes('1annee') || raw === '1') return '1ere';
  if (raw.includes('2eme') || raw.includes('2annee') || raw === '2') return '2eme';
  if (raw.includes('3eme') || raw.includes('3annee') || raw === '3') return '3eme';
  if (raw.includes('4eme') || raw.includes('4annee') || raw === '4') return '4eme';
  if (raw.includes('5eme') || raw.includes('5annee') || raw === '5') return '5eme';
  if (raw.includes('6eme') || raw.includes('6annee') || raw === '6') return '6eme';
  return '';
}

export function classNameFromId(classId, classes = DEFAULT_CLASSES, fallback = '') {
  return classes.find((item) => item.id === classId)?.name || fallback || classId || 'Classe non affectée';
}

export function studentClassId(student, classes = DEFAULT_CLASSES) {
  if (student?.classId && classes.some((item) => item.id === student.classId)) return student.classId;
  return resolveLegacyClassId(
    student?.classLabel || student?.className || student?.level || student?.requestedLevel || student?.classId,
    classes
  );
}

export function useSchoolClasses({ includeInactive = false } = {}) {
  const [remoteClasses, setRemoteClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'classes'),
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        rows.sort((a, b) => Number(a.order ?? 999) - Number(b.order ?? 999) || String(a.name || a.id).localeCompare(String(b.name || b.id), 'fr'));
        setRemoteClasses(rows);
        setLoading(false);
        setError('');
      },
      (loadError) => {
        console.error('Chargement classes :', loadError);
        setError('Impossible de charger le référentiel des classes.');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const classes = useMemo(() => {
    const source = remoteClasses.length ? remoteClasses : DEFAULT_CLASSES;
    return source.filter((item) => includeInactive || item.status !== 'inactive');
  }, [remoteClasses, includeInactive]);

  return {
    classes,
    loading,
    error,
    usingFallback: remoteClasses.length === 0
  };
}
